import { BaseAPI } from './base';

export interface WorklistItem {
  worklist_item_id: string;
  
  // Support both flat and nested patient structures for backward compatibility
  patient_name?: string;
  patient_id?: string;
  patient?: {
    patient_id: string;
    patient_name: string;
    first_name_1?: string;
    first_name_2?: string;
    last_name_1?: string;
    last_name_2?: string;
    first_name?: string;
    last_name?: string;
    birth_date?: string;
    sex?: string;
    email?: string;
    phone?: string;
    medical_record_number?: string;
    estimated_due_date?: string;
    gestational_age_weeks?: number;
    gestational_age_days?: number;
  };
  
  // Support both flat and nested study structures
  accession_number?: string;
  study_description?: string;
  modality?: string;
  study?: {
    accession_number: string;
    study_description: string;
    modality: string;
    study_instance_uid?: string;
    procedure_code_value?: string;
    procedure_code_meaning?: string;
  };
  
  scheduled_date: string;
  scheduled_time?: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  scheduled_physician?: string;  // Doctor who will perform the procedure
  requesting_physician?: string;  // User who created the schedule
  scheduled_station?: string;
  study_instance_uid?: string;
  
  // Office-Machine Assignment fields
  doctor_office_id?: string;
  office_name?: string;  // Populated by backend or frontend lookup
  ultrasound_machine_id?: string;
  machine_name?: string;  // Populated by backend or frontend lookup

  /** Report / exam type id (e.g. ecografia_tiroides) */
  exam_type_id?: string;
  
  // Obstetric information (for DICOM worklist)
  estimated_due_date?: string;
  gestational_age?: string;
  
  created_at: string;
  updated_at: string;
}

export interface WorklistQuery {
  patient_name?: string;
  patient_id?: string;
  accession_number?: string;
  modality?: string;
  status?: string;
  priority?: string;
  date_from?: string;
  date_to?: string;
  scheduled_station?: string;
  requesting_physician?: string;
  scheduled_physician?: string;
  skip?: number;
  limit?: number;
}

class WorklistAPI extends BaseAPI {
  async getWorklistItems(query?: WorklistQuery): Promise<WorklistItem[]> {
    const params = new URLSearchParams();
    
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    return this.request<WorklistItem[]>({
      method: 'GET',
      url: `/api/v1/worklist/items${params.toString() ? `?${params}` : ''}`
    });
  }

  async getWorklistItem(itemId: string): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'GET',
      url: `/api/v1/worklist/items/${itemId}`
    });
  }

  async updateItemStatus(itemId: string, status: WorklistItem['status']): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'PATCH',
      url: `/api/v1/worklist/items/${itemId}/status`,
      data: status // Send status directly as expected by backend
    });
  }

  async createWorklistItem(data: {
    patient: {
      patient_id: string;
      patient_name: string;
      birth_date?: string;
      sex?: string;
      email?: string;
      phone?: string;
      estimated_due_date?: string;
      gestational_age_weeks?: number;
      gestational_age_days?: number;
    };
    study: {
      accession_number: string;
      study_description: string;
      modality: string;
    };
    scheduled_date: string;
    scheduled_time?: string;
    scheduled_physician?: string;
    requesting_physician?: string;
    priority?: 'routine' | 'urgent' | 'emergency' | 'stat';
    doctor_office_id: string;
    ultrasound_machine_id: string;
    estimated_due_date?: string;
    gestational_age?: string;
    exam_type_id?: string;
  }): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'POST',
      url: '/api/v1/worklist/items',
      data
    });
  }

  async updateWorklistItem(itemId: string, data: Partial<{
    scheduled_date: string;
    scheduled_time?: string;
    scheduled_physician?: string;
    requesting_physician?: string;
    priority?: 'routine' | 'urgent' | 'emergency' | 'stat';
    status?: string;
    estimated_due_date?: string;
    gestational_age?: string;
    doctor_office_id?: string;
    ultrasound_machine_id?: string;
    scheduled_station_ae_title?: string;
    exam_type_id?: string;
  }>): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'PUT',
      url: `/api/v1/worklist/items/${itemId}`,
      data
    });
  }

  async getActiveItems(): Promise<WorklistItem[]> {
    return this.request<WorklistItem[]>({
      method: 'GET',
      url: '/api/v1/worklist/active'
    });
  }

  async getItemsByDateRange(startDate: string, endDate: string): Promise<WorklistItem[]> {
    return this.request<WorklistItem[]>({
      method: 'GET',
      url: `/api/v1/worklist/by-date-range?start_date=${startDate}&end_date=${endDate}`
    });
  }

  async getItemsByModality(modality: string): Promise<WorklistItem[]> {
    return this.request<WorklistItem[]>({
      method: 'GET',
      url: `/api/v1/worklist/by-modality/${modality}`
    });
  }

  async deleteWorklistItem(itemId: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/api/v1/worklist/items/${itemId}`
    });
  }

  async bindStudy(
    worklistItemId: string,
    studyInstanceUid: string,
    patientId: string,
  ): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'POST',
      url: `/api/v1/worklist/items/${worklistItemId}/bind-study`,
      data: {
        study_instance_uid: studyInstanceUid,
        patient_id: patientId,
      },
    });
  }

  async createFromOrphanStudy(payload: {
    patient_id: string;
    exam_type_id: string;
    study_instance_uid: string;
    scheduled_date: string;
    doctor_office_id: string;
    ultrasound_machine_id: string;
    study_description?: string;
    scheduled_time?: string;
    scheduled_physician?: string;
    requesting_physician?: string;
  }): Promise<WorklistItem> {
    return this.request<WorklistItem>({
      method: 'POST',
      url: '/api/v1/worklist/items/from-orphan-study',
      data: payload,
    });
  }

  async healthCheck() {
    return this.request({
      method: 'GET',
      url: '/api/v1/worklist/health'
    });
  }
}

export const worklistAPI = new WorklistAPI();
